// src/features/ucits-etf/lib/screener.ts
//
// Pure, framework-free screener logic. Deliberately free of React / Zustand /
// axios so it can be unit-tested in isolation (map UI filter state -> API
// payload) and reused by both the live control panel and the Reset action
// without any risk of drift. Keeping this layer pure is the single most
// valuable thing for testability.

import type { ScreenerRequest } from '../types';

/** The two dividend policies the backend vocabulary ever returns. */
export type DividendPolicy = 'Accumulating' | 'Distributing';

/**
 * The screener's user-facing filter state, in the UNITS the UI works in
 * (expense as a percent, AUM in millions, volume in thousands). The mapping
 * to raw backend units lives entirely in `buildScreenerPayload` so the
 * component never has to remember the conversions.
 */
export interface ScreenerFilters {
    /** Max expense ratio as a percent, e.g. 0.5 -> "0.50%". */
    maxExpense: number;
    /** Min AUM in millions of the fund's currency. */
    minAumMil: number;
    /** Min average daily volume in thousands of shares (0 = "Any"). */
    minVolK: number;
    /** Restrict to UCITS-domiciled funds. */
    ucitsOnly: boolean;
    /** AND-semantics sector chips; empty = no sector filter. */
    selectedSectors: string[];
    /** '' = no dividend-policy filter. */
    dividendPolicy: DividendPolicy | '';
}

/**
 * Canonical default filter state. Single source of truth for the initial
 * mount screen, the Reset action, and the persisted store's fallback, so the
 * three can never drift apart.
 */
export const DEFAULT_FILTERS: ScreenerFilters = {
    maxExpense: 0.5,
    minAumMil: 100,
    minVolK: 0,
    ucitsOnly: true,
    selectedSectors: [],
    dividendPolicy: '',
};

/** How many rows the backend should return per page. */
export const DEFAULT_SCREEN_LIMIT = 50;

/** Page size for the screener grid — one backend request per page. */
export const PAGE_SIZE = DEFAULT_SCREEN_LIMIT;

/**
 * Map UI filter state to the backend POST /ucits-etfs/screen payload. Pure &
 * deterministic:
 *   - millions -> raw currency units (min_aum),
 *   - thousands -> raw share count, or `null` when 0 so the backend skips the
 *     liquidity scan entirely ("Any"),
 *   - '' dividend policy / empty sectors are sent as `null` / `[]` so optional
 *     filters are null-skipped rather than triggering needless equality scans,
 *   - `offset` pages over the AUM-desc ordering (0-based row offset).
 */
export const buildScreenerPayload = (
    filters: ScreenerFilters,
    limit: number = DEFAULT_SCREEN_LIMIT,
    offset: number = 0,
): ScreenerRequest => ({
    max_expense_ratio: filters.maxExpense,
    min_aum: Math.round(filters.minAumMil * 1_000_000),
    min_liquidity:
        filters.minVolK > 0 ? Math.round(filters.minVolK * 1_000) : null,
    sectors: filters.selectedSectors,
    dividend_policy: filters.dividendPolicy || null,
    is_ucits_only: filters.ucitsOnly,
    limit,
    offset,
});

/** One entry in a rendered pagination control. */
export type PageItem = number | 'ellipsis';

/**
 * Compute a compact, gap-collapsed page list for the pagination bar. Always
 * surfaces the first & last page plus the current page and its immediate
 * neighbours, inserting an 'ellipsis' marker wherever a numeric gap is
 * skipped — e.g. buildPageList(5, 20) -> [1,'ellipsis',4,5,6,'ellipsis',20].
 * Pure & deterministic, so it can be unit-tested in isolation.
 */
export const buildPageList = (current: number, total: number): PageItem[] => {
    if (total <= 1) return [1];
    const wanted = new Set<number>([
        1,
        total,
        current,
        current - 1,
        current + 1,
    ]);
    const sorted = [...wanted]
        .filter((p) => p >= 1 && p <= total)
        .sort((a, b) => a - b);

    const out: PageItem[] = [];
    let prev = 0;
    for (const p of sorted) {
        if (p - prev > 1) out.push('ellipsis');
        out.push(p);
        prev = p;
    }
    return out;
};
